import React from 'react';
import { ArrowRight, Eye, ShoppingBag, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getProductHeroImage } from '../data/productHeroImages';
import { getCategoryIdentity, BADGE_STYLES, CARD_TYPOGRAPHY } from '../data/productVisualSystem';

/**
 * Reusable Product Card — Part of the Lumera Product Visual System (Phase 4).
 *
 * Structural rules are shared (ratios, spacing, typography, hover),
 * while each category brings its own visual personality via accent
 * colors and background tints.
 *
 * Props:
 * - product:       Product data object from products.js
 * - category:      Category string (e.g. "Business", "Baby")
 * - categoryLabel: Display name (e.g. "Baby & Family")
 * - demoPages:     Demo page renderers (optional, for fallback preview)
 * - onPreview:     Preview click handler
 * - onAction:      Primary action (use template / try free)
 * - onBuy:         Buy action (when showBuy=true)
 * - showBuy:       Show dual CTA (try free + buy)
 * - variant:       'showcase' (default) | 'compact'
 */
const ProductCard = ({
  product,
  category,
  categoryLabel,
  demoPages,
  onPreview,
  onAction,
  onBuy,
  showBuy = false,
  variant = 'showcase',
}) => {
  const heroImg = getProductHeroImage(product.slug);
  const identity = getCategoryIdentity(category);
  const badge = product.label ? BADGE_STYLES[product.label] : null;
  const demo = demoPages?.[product.title];

  if (!demo && !heroImg) return null;

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative rounded-xl overflow-hidden bg-white border border-[#DDD4C8]/60 shadow-[0_2px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(74,53,37,0.10)] hover:-translate-y-2 transition-all duration-500 flex flex-col"
    >
      {/* ─── Image Area ─── */}
      <div className="relative cursor-pointer overflow-hidden" onClick={onPreview}>
        <div className={variant === 'compact' ? 'h-[200px]' : 'h-[300px] md:h-[320px]'}>
          {heroImg ? (
            <img
              src={heroImg.url}
              alt={heroImg.alt}
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              style={{ objectPosition: heroImg.focal || 'center' }}
              loading="lazy"
            />
          ) : demo ? (
            <div
              className="w-full h-full px-8 pt-8 pb-6 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${identity.bg}, ${product.colors?.bg || identity.bg})` }}
            >
              <div className="relative w-full max-w-[220px]">
                <div className="absolute inset-0 rounded-lg bg-white/40 shadow-sm" style={{ transform: 'rotate(3deg) translateX(6px) translateY(-4px) scale(0.94)' }} />
                <div className="absolute inset-0 rounded-lg bg-white/60 shadow-md" style={{ transform: 'rotate(1.5deg) translateX(3px) translateY(-2px) scale(0.97)' }} />
                <div className="relative bg-white rounded-lg shadow-lg overflow-hidden aspect-[3/4]">
                  {demo.pages[0]({ accent: product.colors?.accent || identity.accent })}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Gradient overlay — pointer-events-none so clicks pass through */}
        {heroImg && <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent pointer-events-none" />}

        {/* Category pill */}
        <div
          className="absolute top-4 left-4 text-[10px] font-semibold uppercase tracking-[0.12em] px-3 py-1 rounded-full bg-white/95 backdrop-blur-md border border-white/40 shadow-sm"
          style={{ color: identity.pillText }}
        >
          {categoryLabel || category}
        </div>

        {/* Badge */}
        {badge && (
          <div className={`absolute top-4 right-4 ${CARD_TYPOGRAPHY.badge} ${badge.cls}`}>
            {badge.text}
          </div>
        )}

        {/* Hover preview overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out pointer-events-none">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg px-5 py-2.5 shadow-xl flex items-center gap-2 scale-90 group-hover:scale-100 transition-transform duration-300">
            <Eye className="w-4 h-4 text-[#4A3525]" />
            <span className="text-xs font-bold text-[#4A3525] tracking-wide">Preview</span>
          </div>
        </div>
      </div>

      {/* ─── Content Area ─── */}
      <div className="p-5 md:p-6 flex flex-col flex-1">
        {/* Category accent strip */}
        <div className="w-8 h-[2px] rounded-full mb-3" style={{ backgroundColor: identity.accent + '60' }} />

        <h3
          className="font-bold text-[#4A3525] text-base md:text-lg leading-snug tracking-tight group-hover:text-[#B49A88] transition-colors duration-300 mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {product.title}
        </h3>
        <p className="text-xs md:text-sm text-[#8A7D72] leading-relaxed line-clamp-2 mb-3">{product.sub}</p>

        {/* Commercial license badge */}
        {product.commercialPriceUsd && (
          <div data-testid="license-badge" className="mb-4 inline-flex items-center gap-1.5 bg-[#F5F0EB]/90 border border-[#E8DFC8]/60 text-[#8B7B6B] px-2.5 py-1 rounded-full">
            <Shield className="w-3 h-3" />
            <span className="text-[10px] font-sans font-medium">Commercial License available</span>
          </div>
        )}

        {/* Spacer to push buttons to bottom */}
        <div className="flex-1" />

        {/* CTA Area */}
        {showBuy ? (
          <div className="space-y-2">
            <Button
              data-testid={`try-preview-${product.slug}`}
              onClick={onAction}
              className="w-full bg-[#EDE3D6] text-[#4A3525] hover:bg-[#E4D8C8] rounded-full py-5 font-semibold text-[13px] tracking-[0.02em] shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all duration-300"
            >
              Try free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              data-testid={`buy-product-${product.slug}`}
              onClick={onBuy}
              variant="outline"
              className="w-full rounded-full py-4 font-medium text-[13px] tracking-[0.02em] border-[#DDD4C8] text-[#8A7D72] hover:bg-[#F7F2EC] transition-all duration-300"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {product.personalPriceUsd ? `From $${product.personalPriceUsd.toFixed(2)}` : product.price}
            </Button>
          </div>
        ) : (
          <Button
            data-testid={`use-template-${product.slug}`}
            onClick={onAction}
            className="w-full bg-[#EDE3D6] text-[#4A3525] hover:bg-[#E4D8C8] rounded-full py-5 font-semibold text-[13px] tracking-[0.02em] shadow-sm hover:shadow-md hover:-translate-y-[1px] transition-all duration-300"
          >
            Use this template <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
